import { Box, Button, Card, CardContent, Checkbox, FormControl, Grid, IconButton, InputLabel, ListItemText, MenuItem, OutlinedInput, Popover, Select, TextField, Typography } from "@mui/material"
import { HelpOutline } from "@mui/icons-material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { changeUserPassword, getUSerDetails, updateUserProfile } from "../../services/user.service";
import { Modal } from "../../ui";
import { uniqueId } from "lodash";
import { useAppDispatch } from "../../store/hooks";
import { globalNotification, toggleAlert } from "../../store/resuders/app.reducer";
import { users } from "../../assets";

export const UserProfile = ()=> {
    const { t } = useTranslation();
    const [open, isOpen] = useState('');
    const [submit, setSubmit] = useState(false);
    const [passwordRulesAnchor, setPasswordRulesAnchor] = useState<HTMLElement | null>(null);
    const dispatch = useAppDispatch();

    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            username: '',
            newPassword:'',
            confirmPassword:'',
            projects: [],
            role: 'ROLE_ADMIN'

        },
        onSubmit: async (values: any) => {
            const result = await updateUserProfile(values, dispatch);
            if(result) dispatch(globalNotification(t('User is updated')));
        }
    })

    const changePassword = async ()=> {
        setSubmit(true);

        if(!formik.values?.newPassword ||  formik.values?.confirmPassword !== formik.values?.newPassword) {
            return;
        }

        const pwd = formik.values?.newPassword || '';
        const errors: string[] = [];
        if (pwd.length < 8) errors.push(t('Password must be at least 8 characters'));
        if (!/[A-Z]/.test(pwd)) errors.push(t('Password must contain an uppercase letter'));
        if (!/[a-z]/.test(pwd)) errors.push(t('Password must contain a lowercase letter'));
        if (!/[0-9]/.test(pwd)) errors.push(t('Password must contain a number'));
        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) errors.push(t('Password must contain a symbol'));
        if (errors.length > 0) {
            dispatch(toggleAlert(errors.join(', ')));
            return;
        }

        const result = await changeUserPassword({
            newPassword:formik.values?.newPassword,
        },dispatch);
        if(result) {
            dispatch(globalNotification(t('New Password Saved.')));
            formik?.setFieldValue('newPassword', '');
            formik?.setFieldValue('confirmPassword', '');
            isOpen('');
        }
    }

    const openModal = ()=> {
        setSubmit(false);
        isOpen(uniqueId());
    }

    useEffect(()=> {
        const getUser = async()=> {
            const user = await getUSerDetails();
            formik.setValues(user);
        }
        getUser();
    },[]);

    return (
    <>
        <div className="flex-center mb-16 pl-20 mt-16">
            <img src={users} alt="" /> 
            <Typography variant="subtitle2">{t('Admin')} </Typography>
            <Typography variant="subtitle2">{'>'}</Typography>
            <Typography variant="subtitle2">{t('User Profile')}</Typography>
        </div>
        <Card className="h-full">
            <CardContent>
                <form onSubmit={formik.handleSubmit}>
                    <div className="form-header flex-start">
                        <Typography variant="subtitle1">{t('Edit User : ')}{formik.values?.firstName}{' '} {formik.values?.lastName}</Typography>
                    </div>
                    <Grid container className="pl-20">
                        <Grid item xs={6}  className="pt-32 flex gap-24 ">
                            <TextField id="firstName" name="firstName" onChange={formik.handleChange} fullWidth label={t("First Name")} value={formik.values?.firstName} />
                            <TextField id="lastName" name="lastName" onChange={formik.handleChange} fullWidth label={t("Last Name")} value={formik.values?.lastName} />
                            <TextField id="username" name="username" onChange={formik.handleChange} fullWidth label={t("User Name")} value={formik.values?.username} />
                        </Grid>

                        <Grid item xs={12} className="pt-32">
                            <Button variant="contained" onClick={openModal} color="primary">{t('Reset Password')}</Button>
                        </Grid>
                        {(formik.values?.role === 'ROLE_USER') && (
                            <Grid item xs={6}  className="pt-32 flex gap-24 ">
                                <FormControl sx={{ width: "100%" }}>
                                    <InputLabel id="project">{t("Project")}</InputLabel>
                                    <Select
                                        labelId="project"
                                        id="project"
                                        label={t("Project")}
                                        name="projects"
                                        multiple
                                        value={formik.values?.projects || []}
                                        // input={<OutlinedInput label="Projects" />}
                                        renderValue={() => formik.values?.projects?.map((project: any) => project.name).join(", ")}
                                        >
                                        {formik.values?.projects.map((option: any) => (
                                            <MenuItem key={option?.testProjectID} value={option}>
                                            <Checkbox
                                                checked={true}
                                            />
                                            <ListItemText primary={option?.name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            
                            </Grid>
                        )}
                    </Grid>
                
                    <div className="profile-save-button">
                        <Button variant="contained" type="submit" color="primary">{t('Save Changes')}</Button>
                    </div>
                </form>
            </CardContent>
        </Card>
            <Modal
                isOpen={open}
                title="Change Password"
                small={true}
                body={
                    <div className='reminder'>
                       <Grid container className="pl-20" spacing={4}>
                            <Grid item xs={12} sx={{ position: "relative" }}>
                                <TextField error={submit && !formik.values?.newPassword} id="newPassword" type="password" name="newPassword"
                                            onChange={formik.handleChange} fullWidth label={t("New Password")} value={formik.values?.newPassword} />
                                <IconButton
                                    size="small"
                                    sx={{ position: "absolute", top: 0, right: -8 }}
                                    onClick={(e) => setPasswordRulesAnchor(e.currentTarget)}
                                >
                                    <HelpOutline fontSize="small" />
                                </IconButton>
                                <Popover
                                    open={Boolean(passwordRulesAnchor)}
                                    anchorEl={passwordRulesAnchor}
                                    onClose={() => setPasswordRulesAnchor(null)}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                >
                                    <Box sx={{ p: 2, maxWidth: 300 }}>
                                        <Typography variant="subtitle2" gutterBottom>{t('Password Requirements')}</Typography>
                                        <ul style={{ margin: 0, paddingLeft: 20, listStyleType: 'disc' }}>
                                            <li><Typography variant="body2">{t('Minimum 8 characters')}</Typography></li>
                                            <li><Typography variant="body2">{t('At least one uppercase letter (A\u2013Z)')}</Typography></li>
                                            <li><Typography variant="body2">{t('At least one lowercase letter (a\u2013z)')}</Typography></li>
                                            <li><Typography variant="body2">{t('At least one number (0\u20139)')}</Typography></li>
                                            <li><Typography variant="body2">{t('At least one symbol (e.g. !@#$%)')}</Typography></li>
                                        </ul>
                                    </Box>
                                </Popover>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField error={submit && !formik.values?.confirmPassword} id="confirmPassword" type="password" name="confirmPassword" 
                                            onChange={formik.handleChange} fullWidth label={t("Confirm Password")} value={formik.values?.confirmPassword} />
                            </Grid>

                            <Grid item xs={12} className="pt-32">
                                <Button variant="contained" onClick={changePassword} color="primary">{t('Change Password')}</Button>
                            </Grid>
                    </Grid>
                    </div>}
                deleteConfirmation={false}
            />
     </>
    )
}